import { useRef } from 'react';
import type { CSSProperties, MouseEvent, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import type { ChartSeriesSpec, DailySeriesRow } from './types';
import { MONTHS_ABBR } from '../../lib/dates';
import { haptic } from '../../lib/haptics';

// SVG uses a 0–100 viewBox so sub-pixel rounding stays stable across
// viewport sizes; axis labels are HTML-overlaid for crisp anti-aliasing.
// Carried verbatim from _prototype/bookings.jsx.
export const VBX = 100;
export const VBY = 100;

export function chartFormatDay(iso: string): string {
  const [, m, d] = iso.split('-').map(Number);
  return `${d} ${MONTHS_ABBR[(m ?? 1) - 1]}`;
}

// One x-axis tick: the series index it sits at + the label it shows.
export interface AxisTick {
  i: number;
  label: string;
}

export interface ChartGeometry {
  yMax: number;
  yTicks: number[];
  xTicks: AxisTick[];
}

// yMax scales to the tallest STACK of the active bands — so the y-axis fits the
// status total in status mode and the (smaller) occupancy total in area mode.
export function bandSum(row: DailySeriesRow, bands: ChartSeriesSpec[]): number {
  return bands.reduce((s, b) => s + (row[b.key] || 0), 0);
}

export function useChartGeometry(series: DailySeriesRow[], bands: ChartSeriesSpec[]): ChartGeometry {
  const yMax = Math.max(
    series.reduce((m, p) => Math.max(m, bandSum(p, bands)), 0),
    5,
  );
  const yTickCount = 4;
  const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) =>
    Math.round((yMax * i) / yTickCount),
  );
  const xTicks = spacedTicks(series.length, Math.ceil(series.length / 10))
    .map(({ i }) => ({ i, label: chartFormatDay(series[i]!.date) }));
  return { yMax, yTicks, xTicks };
}

// WHICH POINTS GET AN X-AXIS LABEL. One rule, shared by every day-series chart,
// because they all had the same defect independently.
//
// Regular ticks every `every`th point, plus the LAST one always — the end of the
// window is the label a reader looks for first. The catch is the interaction:
// forcing the last one in does not remove the regular tick beside it. A 92-day
// window ticking every 10 labels 90 AND 91, two dates a pixel apart, printed on
// top of each other.
//
// So a regular tick is dropped once it comes within half a step of the end. Half
// a step, not one: the labels are short and the axis is wide, and a stricter rule
// would strip a tick that had room.
export function spacedTicks(n: number, every: number): { i: number }[] {
  const last = n - 1;
  const step = Math.max(1, every);
  const out: { i: number }[] = [];
  for (let i = 0; i < n; i++) {
    // `i < last - step / 2` is the collision guard; `i === last` re-admits the
    // end point itself, which that guard would otherwise have excluded.
    if ((i % step === 0 && i < last - step / 2) || i === last) out.push({ i });
  }
  return out;
}

// The shared hover-index rule for continuous (mousemove-tracked) charts: the
// cursor's fraction across the wrap, snapped to the nearest of n points.
export function hoverIndexFromEvent(e: MouseEvent<HTMLElement>, n: number): number {
  const r = e.currentTarget.getBoundingClientRect();
  const frac = r.width ? (e.clientX - r.left) / r.width : 0;
  return Math.max(0, Math.min(n - 1, Math.round(frac * (n - 1))));
}

// The shared SVG line-path builder: M/L commands over n points, coordinates
// fixed to 2dp so sub-pixel rounding stays stable across viewport sizes.
export function linePath(n: number, pointAt: (i: number) => [number, number]): string {
  return Array.from({ length: n }, (_, i) => {
    const [x, y] = pointAt(i);
    return (i === 0 ? 'M' : 'L') + x.toFixed(2) + ',' + y.toFixed(2);
  }).join(' ');
}

// THE CHART SHELL — one card, one head, one fixed-height canvas. Every chart on
// the app renders through this, and every STATE of a chart renders through it
// too (drawn, loading, empty, refused).
//
// That is what stops the page moving. A chart is swapped for another by a
// dropdown and then waits on a fetch, so any state that omits part of the shell
// reflows the page twice on one click — once in, once out. Before this existed
// the money chart's loading state was a bare canvas with no head (a title-row
// shorter than the chart replacing it) and its empty state was `.bookings-empty`,
// a 60px dashed panel beside a ~560px card.
//
// The head is always REAL. Title and legend are known before the data is, and
// blanking what is already known makes the wait look longer than it is.
export function ChartFrame({
  className = 'bookings-chart',
  title,
  legend,
  busy,
  dim,
  children,
}: {
  // The card variant. Default is the full-page chart card; Finances adds
  // `fin-chart` for its tighter canvas, and the Account cards pass
  // `account-chart`, which is headless because the stat card around it already
  // carries the title.
  className?: string;
  // Omitted → no head at all. A head with an empty title is a blank row that
  // still takes space, which is worse than not having one.
  title?: ReactNode;
  legend?: ReactNode;
  // Announce a first load. NOT set for a refresh of data already on screen.
  busy?: boolean;
  // Hold-and-dim: the content is stale rather than absent (a date step).
  dim?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={className + (dim ? ' is-pending' : '')} aria-busy={busy || undefined}>
      {title !== undefined && (
        <div className="chart-head">
          <div className="chart-title">{title}</div>
          {legend}
        </div>
      )}
      <div className="chart-canvas">{children}</div>
    </div>
  );
}

// A message where the plot would be — inside the SAME canvas, so an empty or
// refused window is the same box as a drawn one.
export function ChartNote({ children }: { children: ReactNode }) {
  return <div className="chart-note">{children}</div>;
}

// How much of a bar's SLOT is empty space, per side.
//
// DENSE is for a day series — dozens to hundreds of bars, where the gap only has
// to separate neighbours. It was 0.15 in the occupancy chart and 0.12 in the
// money chart: two undocumented numbers making the same kind of chart look
// different for no stated reason.
//
// SPARSE is for a handful of labelled categories, where the same ratio would
// read as one solid block rather than as distinct bars. A genuinely different
// design decision, named rather than left as a third magic number.
export const BAR_SLOT_INSET = 0.12;
export const CATEGORY_BAR_SLOT_INSET = 0.18;

// Placeholder bars on the canvas the real ones will occupy: the SHAPE of what is
// coming rather than a grey slab, in the same inset box as `.chart-svg-wrap` so
// the swap to real bars lands in place.
//
// `bars` should be the number of points the caller EXPECTS, so the placeholders
// are the width the real bars will be. A fixed count drew a handful of fat
// columns that then became ninety narrow ones — the skeleton has to promise the
// right shape or it is just decoration.
//
// The gap is DERIVED from BAR_SLOT_INSET, the same ratio the real bars use, so
// the two can never drift. Flex gap is a share of the container, not of a slot:
// each slot is 100/n of the width, of which 2×inset is gap.
//
// Heights are derived from the index too, not a stored list — any count works,
// and it renders identically every time. A skeleton that danced differently on
// each load would draw the eye to itself, which is the opposite of the job.
export function ChartSkeleton({ bars }: { bars: number }) {
  const n = Math.max(1, bars);
  return (
    <div
      className="chart-skeleton"
      aria-hidden="true"
      style={{ gap: `${((BAR_SLOT_INSET * 2) / n) * 100}%` }}
    >
      {Array.from({ length: n }, (_, i) => (
        <span
          key={i}
          className="chart-skeleton-bar"
          // Two out-of-phase waves: enough variation to read as data, no
          // randomness to make it unstable between renders.
          style={{ height: `${45 + Math.round(22 * (Math.sin(i * 1.7) + 1) + 8 * (Math.sin(i * 0.6) + 1))}%` }}
        />
      ))}
    </div>
  );
}

// Takes the three fields it actually READS, not the full ChartSeriesSpec. A
// legend has no business requiring `line`, and demanding a `SeriesKey` shut it
// out of every chart outside the booking domain — the money chart had to
// hand-roll the same markup because 'earned' is not a booking status.
// ChartSeriesSpec satisfies this structurally, so existing callers are unchanged.
export interface ChartLegendBand {
  key: string;
  label: string;
  fill: string;
}

export function ChartStatusLegend({ bands }: { bands: ChartLegendBand[] }) {
  return (
    <div className="chart-legend">
      {bands.map((s) => (
        <span key={s.key} className="chart-legend-item">
          <span className="sw" style={{ background: s.fill }} /> {s.label}
        </span>
      ))}
    </div>
  );
}

// One swatch+label+value line in the hover card.
export interface ChartTooltipRow {
  key: string | number;
  sw: string; // swatch colour
  label: ReactNode;
  value: ReactNode;
  zero?: boolean; // dims the row (a band with nothing that day)
}

// The generic hover card every chart shares — head line, optional swatch rows,
// optional total line. Positioned by the caller's left%/top% anchor; the card
// sits just above the anchored point, flipping below when the point is high
// enough (anchorPct < 40) that an above-card would clip the top.
//
// `lead` and `notes` are the SINGLE-SERIES shape: one figure worth reading big,
// then plain supporting lines. A one-series chart rendered as swatch rows says
// "Earned €1,610" — a swatch that distinguishes nothing and a label that
// restates the bar's own height.
export function ChartTooltip({
  head,
  lead,
  notes = [],
  rows = [],
  total,
  leftPct,
  anchorPct = 0,
}: {
  head: ReactNode;
  lead?: ReactNode;
  notes?: ReactNode[];
  rows?: ChartTooltipRow[];
  total?: { label: ReactNode; value: ReactNode; valueStyle?: CSSProperties };
  leftPct: number;
  // Vertical position of the hovered point as a % of the chart height (0 = top).
  anchorPct?: number;
}) {
  const below = anchorPct < 40;
  return (
    <div
      className={'chart-tooltip ' + (below ? 'below' : 'above')}
      // The horizontal position is offered as a CUSTOM PROPERTY, not as `left`,
      // so the stylesheet can clamp it to the plot. Held on the last day at 320
      // the card ran to 334 in a 320px viewport — 14px of horizontal overflow,
      // in breach of AC17, and showing precisely while the readout is up.
      style={{ ['--tt-left' as string]: leftPct + '%', top: anchorPct + '%' }}
    >
      <div className="chart-tooltip-head">{head}</div>
      {lead != null && <div className="chart-tooltip-lead mono">{lead}</div>}
      {notes.map((n, i) => (
        <div key={'n' + i} className="chart-tooltip-note">{n}</div>
      ))}
      {rows.map((r) => (
        <div key={r.key} className={'chart-tooltip-row' + (r.zero ? ' zero' : '')}>
          <span className="sw" style={{ background: r.sw }} />
          <span className="lbl">{r.label}</span>
          <span className="mono val">{r.value}</span>
        </div>
      ))}
      {total && (
        <div className="chart-tooltip-total">
          <span>{total.label}</span>
          <span className="mono" style={total.valueStyle}>
            {total.value}
          </span>
        </div>
      )}
    </div>
  );
}

// The bookings-domain wrapper: a day's series row rendered as one tooltip row
// per band, with the stack total. null point → no card (nothing hovered).
export function ChartBandsTooltip({
  point,
  bands,
  leftPct,
  anchorPct = 0,
}: {
  point: DailySeriesRow | null;
  bands: ChartSeriesSpec[];
  leftPct: number;
  anchorPct?: number;
}) {
  if (!point) return null;
  return (
    <ChartTooltip
      head={chartFormatDay(point.date)}
      rows={bands.map((s) => ({
        key: s.key,
        sw: s.fill,
        label: s.label,
        value: point[s.key],
        zero: point[s.key] === 0,
      }))}
      total={{ label: 'Total', value: bandSum(point, bands) }}
      leftPct={leftPct}
      anchorPct={anchorPct}
    />
  );
}

// Invisible per-day hit targets. Only the COUNT matters, so the series is typed
// as an opaque array — the money chart on Daily feeds it FinanceDay rows, not
// DailySeriesRow, and neither needs the primitive to know its shape.
export function ChartHoverSlices({
  series,
  onIndex,
  onClear,
  onSwipe,
}: {
  series: readonly unknown[];
  onIndex: (i: number) => void;
  // End of a touch read — the readout belongs to the finger that summoned it.
  onClear?: () => void;
  // A horizontal drag pages the window. Given only where paging exists (the
  // phone tier's fortnight); absent, a drag does nothing and only the hold
  // reads a day.
  onSwipe?: (dir: 1 | -1) => void;
}) {
  const slotW = VBX / series.length;
  const n = series.length;
  // Touch has no hover, so the same readout is reached by holding: a press that
  // stays put for HOLD_MS starts reading and follows the finger, while a press
  // that travels sideways first is a page turn instead. One surface, two
  // gestures, told apart by what the finger does — the ambiguity is resolved
  // before either fires, so neither can trigger the other by accident.
  const HOLD_MS = 220;
  const SLOP = 8; // movement that cancels the hold
  const SWIPE = 40; // movement that counts as a page
  const st = useRef({ timer: 0, reading: false, x0: 0, y0: 0, id: -1 });

  const indexAt = (clientX: number, el: Element): number => {
    const r = el.getBoundingClientRect();
    if (!r.width) return 0;
    const frac = (clientX - r.left) / r.width;
    return Math.max(0, Math.min(n - 1, Math.floor(frac * n)));
  };

  const end = (e: ReactPointerEvent<SVGRectElement>) => {
    const s = st.current;
    // Only the pointer that started this finishes it. `end` used to run for any
    // release at all, computing a displacement from whatever origin happened to
    // be in the ref — a stale one from a previous touch, or 0 if there had never
    // been one.
    if (s.id !== e.pointerId) return;
    s.id = -1;
    if (s.timer) { window.clearTimeout(s.timer); s.timer = 0; }
    if (s.reading) {
      s.reading = false;
      // A MOUSE KEEPS ITS READOUT. The readout belongs to the finger that
      // summoned it — but a cursor has not gone anywhere, and blanking the day
      // it is still pointing at would be a worse answer than leaving it.
      if (e.pointerType !== 'mouse') onClear?.();
      return;
    }
    const dx = e.clientX - s.x0;
    const dy = e.clientY - s.y0;
    if (onSwipe && Math.abs(dx) > SWIPE && Math.abs(dx) > Math.abs(dy) * 1.5) {
      onSwipe(dx < 0 ? 1 : -1);
    }
  };

  return (
    <>
      {series.map((_, i) => (
        <rect
          key={i}
          x={i * slotW}
          y="0"
          width={slotW}
          height={VBY}
          fill="transparent"
          // POINTER-enter, not mouse-enter. After `touchend`, Chrome synthesises
          // a compatibility mouse sequence — mouseenter included — at the point
          // the finger left. On `onMouseEnter` that re-summoned the readout a
          // millisecond after the lift dismissed it, so on a real handset the
          // tooltip never went away. Compatibility events are not pointer
          // events, so asking for the pointer type settles it.
          onPointerEnter={(e) => {
            if (e.pointerType !== 'mouse') return;
            onIndex(i);
          }}
          onPointerDown={(e) => {
            const s = st.current;
            // A second finger does not join the gesture in progress — it was
            // silently overwriting the first one's origin, so lifting either
            // measured a displacement neither had made.
            if (s.id !== -1) return;
            s.x0 = e.clientX;
            s.y0 = e.clientY;
            s.id = e.pointerId;
            // NO POINTER CAPTURE HERE, deliberately. It was added to keep the
            // gesture on the slice it began on, on the theory that the hover
            // tooltip could swallow the pointerup — but the tooltip has carried
            // `pointer-events: none` all along, so it never could. What capture
            // did instead was pin the gesture to a <rect> that React replaces on
            // the very re-render the hold triggers: the finger lifts, the
            // pointerup goes to an element no longer in the document, `end` never
            // runs, and the readout stays on screen after the finger has gone.
            // Synthetic events could not show this — dispatchEvent re-resolves
            // the locator and hits the NEW rect, so the contract stayed green
            // while a real finger did not work. Hence the real-touch suite.
            //
            // A MOUSE, HOWEVER, GETS NO IMPLICIT CAPTURE — that is the whole
            // asymmetry. Touch is captured by the browser for the life of the
            // gesture; a cursor is not, so a drag that wanders off the plot (the
            // natural way to swipe from an edge day) released over nothing and
            // never paged. Capture for the mouse only: it is what touch already
            // has, and it is what broke touch when applied to both.
            if (e.pointerType === 'mouse') e.currentTarget.setPointerCapture?.(e.pointerId);
            //
            // THE HOLD IS ARMED FOR EVERY POINTER, mouse included. It was touch-
            // only, on the reasoning that hover already reads a day for a cursor
            // — true, and beside the point. People press and hold with a mouse
            // because that is the gesture they were told about; and once the
            // press was being recorded so the swipe could work, a click-and-hold
            // that drifted 40px paged the chart away instead of reading it. One
            // gesture, one arbitration, whatever is doing the pressing.
            const host = e.currentTarget.ownerSVGElement ?? e.currentTarget;
            s.timer = window.setTimeout(() => {
              s.timer = 0;
              s.reading = true;
              // The hold took. Nothing has moved and the finger is covering the
              // column it just selected, so on a device that can say so, this is
              // the only confirmation there is.
              haptic('tick');
              onIndex(indexAt(s.x0, host));
            }, HOLD_MS);
          }}
          onPointerMove={(e) => {
            const s = st.current;
            if (s.id !== e.pointerId) return;
            const host = e.currentTarget.ownerSVGElement ?? e.currentTarget;
            if (s.reading) {
              e.preventDefault();
              onIndex(indexAt(e.clientX, host));
              return;
            }
            if (s.timer && (Math.abs(e.clientX - s.x0) > SLOP || Math.abs(e.clientY - s.y0) > SLOP)) {
              window.clearTimeout(s.timer);
              s.timer = 0;
            }
          }}
          onPointerUp={end}
          onPointerCancel={end}
        />
      ))}
    </>
  );
}

// HTML-overlaid y-axis labels, top tick first. `format` lets a chart show
// euro/short forms; the default renders the raw count.
export function ChartYAxis({
  ticks,
  format,
}: {
  ticks: number[];
  format?: (v: number) => ReactNode;
}) {
  const reversed = [...ticks].reverse();
  return (
    <div className="chart-y-axis">
      {reversed.map((v, i) => (
        <span
          key={i}
          className="mono"
          style={{ top: `${(i / Math.max(1, reversed.length - 1)) * 100}%` }}
        >
          {format ? format(v) : v}
        </span>
      ))}
    </div>
  );
}

// HTML-overlaid x-axis labels, positioned by series index over `count` points.
// Module-private: every caller goes through ChartAxes, which pairs it with the
// y axis. Exported for a while with nobody importing it.
function ChartXAxis({ ticks, count }: { ticks: AxisTick[]; count: number }) {
  return (
    <div className="chart-x-axis">
      {ticks.map((t) => (
        <span
          key={t.i}
          className="mono"
          style={{ left: `${(t.i / Math.max(1, count - 1)) * 100}%` }}
        >
          {t.label}
        </span>
      ))}
    </div>
  );
}

export function ChartAxes({
  yTicks,
  xTicks,
  seriesLength,
  formatY,
}: {
  yTicks: number[];
  xTicks: AxisTick[];
  seriesLength: number;
  formatY?: (v: number) => ReactNode;
}) {
  return (
    <>
      <ChartYAxis ticks={yTicks} format={formatY} />
      <ChartXAxis ticks={xTicks} count={seriesLength} />
    </>
  );
}
